import React, {Dispatch, SetStateAction, useEffect, useRef, useState} from "react";
import {
    TextField,
    Box,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Select,
    Typography,
    ListItemText,
    Alert,
    Tabs,
    Tab,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./quill-overrides.css";
import {COMPANY_MEMBER_ROLES, LANGUAGES} from "../../../utils/enumItemsUtils";
import {CompanyMemberRole} from "../../../types/domain/companyMember";
import {
    AnnouncementTranslatedContent,
    NotificationStatus,
    NotificationTargetPlatform,
} from "../../../types/domain/notification";
import {DateTimePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {useCompanies} from "../../../hooks/companiesHooks";
import {LanguageType} from "../../../types/domain/languageType";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {ZodError} from "zod";

dayjs.extend(utc);

interface AddAnnouncementProps {
    name: string;
    setName: (name: string) => void;
    selectedCompanyIds: string[];
    setSelectedCompanyIds: (ids: string[]) => void;
    allCompanies: boolean;
    setAllCompanies: (values: boolean) => void;
    setSelectedCompanyMemberRoles: (roles: CompanyMemberRole[]) => void;
    selectedCompanyMemberRoles: CompanyMemberRole[];
    status: NotificationStatus;
    setStatus: (status: NotificationStatus) => void;
    setStartTime: (date: string) => void;
    startTime: string | null;
    contentObject: Partial<Record<LanguageType, AnnouncementTranslatedContent>>;
    setContentObject: Dispatch<SetStateAction<Partial<Record<LanguageType, AnnouncementTranslatedContent>>>>;
    platforms: Record<NotificationTargetPlatform, boolean>;
    setPlatforms: Dispatch<SetStateAction<Record<NotificationTargetPlatform, boolean>>>;
    errors?: ZodError;
    submitError?: boolean;
}

const AddAnnouncement: React.FC<AddAnnouncementProps> = ({
    name,
    setName,
    selectedCompanyIds,
    setSelectedCompanyIds,
    allCompanies,
    setAllCompanies,
    setSelectedCompanyMemberRoles,
    selectedCompanyMemberRoles,
    status,
    setStatus,
    setStartTime,
    startTime,
    contentObject,
    setContentObject,
    platforms,
    setPlatforms,
    errors,
    submitError,
}) => {
    const companies = useCompanies();
    const notificationStatuses = Object.values(NotificationStatus);
    const {t} = useTranslation();
    const [activeLang, setActiveLang] = useState(LANGUAGES[0].id);

    const editorRefs = useRef<Record<LanguageType, HTMLDivElement>>({} as Record<LanguageType, HTMLDivElement>);
    const quillRefs = useRef<Record<LanguageType, Quill | null>>({} as Record<LanguageType, Quill | null>);

    useEffect(() => {
        const lang = activeLang as LanguageType;

        if (editorRefs.current[lang] && !quillRefs.current[lang]) {
            quillRefs.current[lang] = new Quill(editorRefs.current[lang], {
                theme: "snow",
                modules: {
                    toolbar: [["bold", "italic", "underline"], ["link"], [{list: "bullet"}]],
                },
            });

            // Initialize editor with stored content
            quillRefs.current[lang]!.root.innerHTML = contentObject[lang]?.content ?? "";

            quillRefs.current[lang]!.on("text-change", () => {
                const html = editorRefs.current[lang]?.querySelector(".ql-editor")?.innerHTML ?? "";
                setContentObject((prev) => ({
                    ...prev,
                    [lang]: {
                        ...prev[lang],
                        content: html,
                    },
                }));
            });
        }
    }, [activeLang, contentObject, setContentObject]);

    function getError(errors?: ZodError, field?: string): string | undefined {
        if (!errors) return;
        const issue = errors.issues.find((i) => i.path[0] === field);
        return issue?.message;
    }

    return (
        <Box sx={{display: "flex", gap: 5, margin: "0 auto"}} className="add-announcement-container">
            <Box sx={{display: "flex", flexDirection: "column", width: "300px", maxWidth: "300px"}}>
                <Box>
                    <Typography variant="h3" sx={{mb: 1}}>
                        {t("add_announcement.announcement").toUpperCase()}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t("add_announcement.name")}
                    </Typography>

                    <TextField
                        variant="outlined"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        placeholder={t("add_announcement.internal")}
                        error={!!getError(errors, "name")}
                        helperText={getError(errors, "name")}
                    />

                    <br />
                    <br />

                    <Typography variant="h3" sx={{mb: 1}}>
                        {t("add_announcement.audience").toUpperCase()}
                    </Typography>

                    <Box sx={{mt: 2}}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {t("add_announcement.platforms")}
                        </Typography>

                        {Object.keys(NotificationTargetPlatform).map((platform) => (
                            <FormControlLabel
                                key={platform}
                                control={
                                    <Checkbox
                                        checked={platforms[platform as NotificationTargetPlatform] || false}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setPlatforms((prev) => ({...prev, [platform]: true}));
                                            } else {
                                                setPlatforms((prev) => ({...prev, [platform]: false}));
                                            }
                                        }}
                                    />
                                }
                                label={t(`add_announcement.platform_${platform.toLowerCase()}`)}
                            />
                        ))}

                        {getError(errors, "platforms") && (
                            <Alert severity="error" sx={{mt: 1}}>
                                {getError(errors, "platforms")}
                            </Alert>
                        )}
                    </Box>

                    <br />

                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t("add_announcement.companies")}
                    </Typography>

                    {!allCompanies ? (
                        <Box sx={{marginBottom: "10px"}}>
                            <Select
                                multiple
                                value={selectedCompanyIds}
                                onChange={(event) => {
                                    const selectedCompanyIds = event.target.value as string[];
                                    setSelectedCompanyIds(selectedCompanyIds);
                                }}
                                renderValue={(selected) => {
                                    return `${selected.length} ${t("add_announcement.selected")}`;
                                }}
                                sx={{width: "100%"}}
                            >
                                {companies.isSuccess
                                    ? companies.data.map((company) => (
                                          <MenuItem
                                              key={company.id}
                                              value={company.id}
                                              sx={{height: 36, minHeight: 36, paddingY: 0.5}}
                                          >
                                              <Checkbox checked={selectedCompanyIds.includes(company.id)} />
                                              <ListItemText primary={company?.properties?.name} />
                                          </MenuItem>
                                      ))
                                    : ""}
                            </Select>
                            <br />
                        </Box>
                    ) : (
                        ""
                    )}

                    {!allCompanies ? (
                        <Box sx={{marginBottom: "10px"}}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("add_announcement.roles")}
                            </Typography>

                            <Select
                                multiple
                                value={selectedCompanyMemberRoles}
                                onChange={(event) => {
                                    const selectedCompanyMemberRoles = event.target.value as CompanyMemberRole[];
                                    setSelectedCompanyMemberRoles(selectedCompanyMemberRoles);
                                }}
                                renderValue={(selected) => {
                                    return `${selected.length} ${t("add_announcement.selected")}`;
                                }}
                                sx={{width: "100%"}}
                            >
                                {COMPANY_MEMBER_ROLES.map((role) => (
                                    <MenuItem key={role.id} value={role.id}>
                                        <Checkbox checked={selectedCompanyMemberRoles.includes(role.id)} />
                                        <ListItemText primary={CompanyMemberRole[role.id]} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    ) : (
                        ""
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox checked={allCompanies} onChange={(e) => setAllCompanies(e.target.checked)} />
                        }
                        label={t("add_announcement.send_announcement_to_all_users")}
                    />

                    {getError(errors, "audience") && (
                        <Alert severity="error" sx={{mt: 1}}>
                            {getError(errors, "audience")}
                        </Alert>
                    )}
                </Box>

                <Box sx={{mt: 3}}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t("add_announcement.status")}
                    </Typography>

                    <Select
                        size="small"
                        value={status}
                        onChange={(event) => {
                            setStatus(event.target.value as NotificationStatus);
                        }}
                        displayEmpty
                        sx={{width: "100%"}}
                    >
                        {notificationStatuses.map((status) => (
                            <MenuItem key={status} value={status}>
                                {status}
                            </MenuItem>
                        ))}
                    </Select>

                    <br />
                    <br />

                    {status === NotificationStatus.SCHEDULED ? (
                        <DateTimePicker
                            sx={{width: "100%"}}
                            label={t("add_announcement.start_time")}
                            value={dayjs(startTime)}
                            onChange={(newValue) => {
                                if (newValue) {
                                    const formatted = newValue.format("YYYY-MM-DDTHH:mm:ss.SSSZ");
                                    setStartTime(formatted);
                                }
                            }}
                        />
                    ) : (
                        ""
                    )}

                    {status === NotificationStatus.SCHEDULED ? (
                        <Alert icon={<InfoIcon fontSize="inherit" />} severity="info" sx={{mt: 2}}>
                            {t("add_announcement.scheduled_announcements_cannot_be_changed_once_submitted")}
                        </Alert>
                    ) : (
                        ""
                    )}

                    {status === NotificationStatus.SENT ? (
                        <Alert icon={<InfoIcon fontSize="inherit" />} severity="info">
                            {t("add_announcement.sent_announcements_cannot_be_changed_once_submitted")}
                        </Alert>
                    ) : (
                        ""
                    )}

                    {status === NotificationStatus.CANCELLED ? (
                        <Alert icon={<InfoIcon fontSize="inherit" />} severity="info">
                            {t("add_announcement.cancelled_announcements_cannot_be_changed_once_submitted")}
                        </Alert>
                    ) : (
                        ""
                    )}
                </Box>
            </Box>

            <Box sx={{display: "flex", flex: 1, flexDirection: "column", overflow: "hidden"}}>
                <Typography variant="h3" sx={{mb: 1}}>
                    {t("add_announcement.message").toUpperCase()}
                </Typography>

                <Tabs
                    value={activeLang}
                    onChange={(_, newValue) => setActiveLang(newValue as LanguageType)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        ".MuiTabs-indicator": {
                            backgroundColor: "primary.main",
                            height: "3px",
                        },
                    }}
                >
                    {Object.values(LANGUAGES).map((lang) => (
                        <Tab key={lang.id} label={lang.label} value={lang.id} />
                    ))}
                </Tabs>

                {Object.values(LANGUAGES).map((lang) => (
                    <Box key={lang.id} role="tabpanel" sx={{mt: 3, display: activeLang === lang.id ? "block" : "none"}}>
                        <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                            <Box sx={{position: "relative"}}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder={t("add_announcement.title")}
                                    value={contentObject[lang.id]?.title ?? ""}
                                    slotProps={{
                                        htmlInput: {
                                            maxLength: 60,
                                        },
                                    }}
                                    onChange={(e) =>
                                        setContentObject({
                                            ...contentObject,
                                            [lang.id]: {
                                                ...contentObject[lang.id],
                                                title: e.target.value,
                                            },
                                        })
                                    }
                                />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{position: "absolute", bottom: 4, right: 8}}
                                >
                                    {contentObject[lang.id]?.title?.length ?? 0}/60
                                </Typography>
                            </Box>

                            {platforms.MOBILE && (
                                <Box sx={{position: "relative"}}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder={t("add_announcement.short_text")}
                                        value={contentObject[lang.id]?.shortText ?? ""}
                                        slotProps={{
                                            htmlInput: {
                                                maxLength: 80,
                                            },
                                        }}
                                        onChange={(e) =>
                                            setContentObject({
                                                ...contentObject,
                                                [lang.id]: {
                                                    ...contentObject[lang.id],
                                                    shortText: e.target.value,
                                                },
                                            })
                                        }
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{position: "absolute", bottom: 4, right: 8}}
                                    >
                                        {contentObject[lang.id]?.shortText?.length ?? 0}/80
                                    </Typography>
                                </Box>
                            )}

                            <div>
                                <div
                                    ref={(el: HTMLDivElement) => {
                                        editorRefs.current[lang.id] = el;
                                    }}
                                />
                            </div>
                            {submitError && (
                                <Alert severity="error" sx={{mt: 1}}>
                                    {t("add_announcement.error_submitting")}
                                </Alert>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default AddAnnouncement;
